import { useState, useEffect } from 'react';

import { useForm } from '@tanstack/react-form';
import {
  InputText,
  Button,
  InputImageUploader,
  TextBlock,
  FormField,
  SubmitButtonWrapper,
  InputPassword,
  Modal,
  toast,
} from '@skillarea/lib-frontend';
import {
  EWithdrawalKycStatus,
  UserUpdateDto,
  UserUpdatePasswordDto,
  createValidator,
} from '@skillarea/lib-shared';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { accountAuthGuard } from './-account-auth-guard';
import styles from './profile.module.scss';

import { AccountLayout } from '~/components/Account/AccountLayout';
import { EnableTotpModal } from '~/components/Account/EnableTotpModal';
import { useEnableTotpFlow } from '~/hooks/useEnableTotpFlow';
import { useWithdrawalKycFlow } from '~/hooks/useWithdrawalKycFlow';
import { uploadStore } from '~/stores/uploadStore';
import {
  useSavePassword,
  useSaveUserData,
  useUserEmail,
  useUserName,
  useUserAvatar,
  useUserMfaEnabled,
  useUserStore,
} from '~/stores/userStore';
import { useWithdrawalKycStore } from '~/stores/withdrawalKycStore';
import { useInputImageUploaderTexts } from '~/hooks/texts/useInputImageUploaderTexts';
import { onboardStore } from '~/stores/onboardStore';

export const Route = createFileRoute('/account/profile')({
  component: ProfilePage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

const userDataValidator = createValidator(UserUpdateDto);
const userPasswordValidator = createValidator(UserUpdatePasswordDto);

function isSuccessfulResult(value: unknown): value is { success: true } {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as { success?: unknown };
  return candidate.success === true;
}

function getKycStatusText(
  t: TFunction<'account'>,
  status: EWithdrawalKycStatus,
): string {
  switch (status) {
    case EWithdrawalKycStatus.approved:
      return t('profile.kyc.status.verified');
    case EWithdrawalKycStatus.pending:
      return t('profile.kyc.status.pending');
    case EWithdrawalKycStatus.resubmission_requested:
      return t('profile.kyc.status.actionRequired');
    case EWithdrawalKycStatus.not_started:
    case EWithdrawalKycStatus.declined:
    case EWithdrawalKycStatus.expired:
    case EWithdrawalKycStatus.abandoned:
    case EWithdrawalKycStatus.revoked:
      return t('profile.kyc.status.notVerified');
    default:
      return String(status);
  }
}

function ProfilePage() {
  const { t } = useTranslation('account');
  const savePassword = useSavePassword();
  const saveUserData = useSaveUserData();
  const name = useUserName();
  const email = useUserEmail();
  const avatarUrl = useUserAvatar();
  const mfaEnabled = useUserMfaEnabled();
  const loadUser = useUserStore((s) => s.loadUser);
  const texts = useInputImageUploaderTexts();
  const [avatar, setAvatar] = useState<
    { name: string; url: string } | undefined
  >(() => (avatarUrl ? { name: '', url: avatarUrl } : undefined));
  const [disableCode, setDisableCode] = useState('');
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isDisableMfaLoading, setIsDisableMfaLoading] = useState(false);

  const kycStatus = useWithdrawalKycStore((s) => s.status);
  const kycCanStart = useWithdrawalKycStore((s) => s.canStart);
  const kycCanContinue = useWithdrawalKycStore((s) => s.canContinue);
  const isKycLoading = useWithdrawalKycStore((s) => s.isLoading);
  const { refreshStatus: refreshKycStatus, startOrContinueKyc } =
    useWithdrawalKycFlow();

  const {
    closeModal: closeEnableTotpModal,
    code: enableCode,
    confirmEnableTotp,
    isLoading: isEnableMfaLoading,
    isModalOpen: isEnableModalOpen,
    openEnableTotp,
    setCode: setEnableCode,
  } = useEnableTotpFlow();

  useEffect(() => {
    if (avatarUrl) {
      setAvatar({ name: '', url: avatarUrl });
    }
  }, [avatarUrl]);

  useEffect(() => {
    void refreshKycStatus();

    const handleFocus = () => {
      void refreshKycStatus();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshKycStatus]);

  const userDataForm = useForm({
    defaultValues: {
      name,
    },
    onSubmit: async ({ value }) => {
      await saveUserData({ name: value.name as string });
    },
    validators: {
      onChange: userDataValidator,
      onSubmit: userDataValidator,
    },
  });

  const passwordForm = useForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      await savePassword(value as UserUpdatePasswordDto);
    },
    validators: {
      onChange: userPasswordValidator,
      onSubmit: userPasswordValidator,
    },
  });

  const handleConfirmDisableTotp = async () => {
    const code = disableCode.trim();
    if (!code) return;

    setIsDisableMfaLoading(true);
    try {
      const result = await onboardStore.getState().disableTotp({
        body: { code },
      });

      if (!isSuccessfulResult(result)) {
        return;
      }

      setIsDisableModalOpen(false);
      setDisableCode('');
      await loadUser();
      toast?.success(t('profile.mfa.toast.disabled'));
    } catch {
      toast?.error(t('profile.mfa.toast.invalidCode'));
    } finally {
      setIsDisableMfaLoading(false);
    }
  };

  return (
    <AccountLayout>
      <div className={styles.content}>
        <div className={styles.avatarColumn}>
          <InputImageUploader
            className={styles.avatarUploader}
            classes={{
              image: styles.avatarWrapper,
              screen: styles.avatarScreen,
              name: 'visually-hidden',
            }}
            onChange={async (value) => {
              setAvatar(value);
              await saveUserData({ avatar: value.url || '' });
            }}
            size="m"
            slot="avatar"
            store={uploadStore}
            texts={texts}
            value={avatar}
          />
        </div>

        <div className={styles.formColumn}>
          <div className={styles.formsRow}>
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault();
                userDataForm.handleSubmit();
              }}
            >
              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('email.label')}
                </TextBlock>
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  disabled
                  placeholder={t('email.placeholder')}
                  textColor="#fff"
                  type="email"
                  value={email}
                />
              </div>
              <TextBlock color="contrast" weight="600">
                {t('profile.nickname.label')}
              </TextBlock>
              <FormField form={userDataForm} name="name">
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  placeholder={t('profile.nickname.placeholder')}
                  textColor="#fff"
                />
              </FormField>

              <SubmitButtonWrapper form={userDataForm}>
                <Button
                  className={styles.saveButton}
                  color="accent"
                  size="s"
                  type="submit"
                  variant="outline"
                >
                  {t('profile.saveChanges')}
                </Button>
              </SubmitButtonWrapper>
            </form>

            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault();
                passwordForm.handleSubmit();
              }}
            >
              <TextBlock color="contrast" weight="600">
                {t('profile.password.current.label')}
              </TextBlock>
              <FormField form={passwordForm} name="oldPassword">
                <InputPassword
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  placeholder={t('profile.password.current.placeholder')}
                  textColor="#fff"
                />
              </FormField>

              <TextBlock color="contrast" weight="600">
                {t('profile.password.new.label')}
              </TextBlock>
              <FormField form={passwordForm} name="newPassword">
                <InputPassword
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  placeholder={t('passwordNew.placeholder')}
                  textColor="#fff"
                />
              </FormField>

              <TextBlock color="contrast" weight="600">
                {t('profile.password.repeat.label')}
              </TextBlock>
              <FormField form={passwordForm} name="confirmPassword">
                <InputPassword
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  placeholder={t('profile.password.repeat.placeholder')}
                  textColor="#fff"
                />
              </FormField>

              <SubmitButtonWrapper form={passwordForm}>
                <Button
                  className={styles.saveButton}
                  color="accent"
                  size="s"
                  type="submit"
                  variant="outline"
                >
                  {t('profile.saveChanges')}
                </Button>
              </SubmitButtonWrapper>
            </form>
          </div>

          <div className={styles.securitySection}>
            <div className={styles.securityHeader}>
              <TextBlock color="contrast" weight="600">
                {t('profile.mfa.title')}
              </TextBlock>
              <TextBlock className={styles.securityStatus} color="secondary">
                {mfaEnabled
                  ? t('profile.mfa.enabled')
                  : t('profile.mfa.disabled')}
              </TextBlock>
            </div>
            <TextBlock color="secondary">
              {t('profile.mfa.description')}
            </TextBlock>
            <div className={styles.mfaActions}>
              {!mfaEnabled ? (
                <Button
                  color="accent"
                  data-testid="mfa-enable-button"
                  disabled={isEnableMfaLoading}
                  onClick={() => {
                    void openEnableTotp();
                  }}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  {t('profile.mfa.enable')}
                </Button>
              ) : (
                <Button
                  color="accent"
                  data-testid="mfa-disable-button"
                  disabled={isDisableMfaLoading}
                  onClick={() => setIsDisableModalOpen(true)}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  {t('profile.mfa.disable')}
                </Button>
              )}
            </div>
          </div>
          <div className={styles.securitySection}>
            <div className={styles.securityHeader}>
              <TextBlock color="contrast" weight="600">
                {t('profile.kyc.title')}
              </TextBlock>
              <TextBlock
                className={styles.securityStatus}
                color="secondary"
                data-testid="withdrawal-kyc-status"
              >
                {getKycStatusText(t, kycStatus)}
              </TextBlock>
            </div>
            <TextBlock color="secondary">
              {t('profile.kyc.description')}
            </TextBlock>
            <div className={styles.mfaActions}>
              {kycCanStart && (
                <Button
                  color="accent"
                  data-testid="withdrawal-kyc-start-button"
                  disabled={isKycLoading}
                  onClick={() => void startOrContinueKyc()}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  {t('profile.kyc.start')}
                </Button>
              )}
              {kycCanContinue && (
                <Button
                  color="accent"
                  data-testid="withdrawal-kyc-continue-button"
                  disabled={isKycLoading}
                  onClick={() => void startOrContinueKyc()}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  {t('profile.kyc.continue')}
                </Button>
              )}
              {(kycStatus === EWithdrawalKycStatus.pending ||
                kycStatus === EWithdrawalKycStatus.resubmission_requested) && (
                <Button
                  color="accent"
                  data-testid="withdrawal-kyc-refresh-button"
                  disabled={isKycLoading}
                  onClick={() => void refreshKycStatus()}
                  size="s"
                  type="button"
                  variant="outline"
                >
                  {t('profile.kyc.refresh')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <EnableTotpModal
        actionsClassName={styles.modalActions}
        code={enableCode}
        contentClassName={styles.modalContent}
        hintClassName={styles.modalHint}
        isLoading={isEnableMfaLoading}
        isOpen={isEnableModalOpen}
        onClose={closeEnableTotpModal}
        onCodeChange={setEnableCode}
        onConfirm={() => {
          void confirmEnableTotp();
        }}
      />

      <Modal
        isOpen={isDisableModalOpen}
        onBeforeClose={() => {
          setIsDisableModalOpen(false);
          setDisableCode('');
        }}
        title={t('profile.mfa.disableModal.title')}
      >
        <div className={styles.modalContent} data-testid="mfa-disable-modal">
          <TextBlock color="contrast" weight="600">
            {t('profile.mfa.disableModal.confirmTitle')}
          </TextBlock>
          <TextBlock className={styles.modalHint} color="secondary">
            {t('profile.mfa.disableModal.hint')}
          </TextBlock>
          <InputText
            backgroundColor="rgba(3, 3, 4, 0.90)"
            data-testid="mfa-disable-code-input"
            onChange={(value) => setDisableCode(value)}
            placeholder={t('profile.mfa.disableModal.placeholder')}
            textColor="#fff"
            value={disableCode}
          />
          <div className={styles.modalActions}>
            <Button
              color="accent"
              data-testid="mfa-disable-submit"
              disabled={!disableCode || isDisableMfaLoading}
              onClick={handleConfirmDisableTotp}
              size="s"
              type="button"
              variant="outline"
            >
              {t('profile.mfa.disableModal.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </AccountLayout>
  );
}
